import api from "api/axios";

export interface WatchlistPost {
  id: number;
  target_id: number;
  target_type: "personal" | "company";
  post_url: string;
  post_text: string | null;
  headline: string | null;
  post_date: string | null;
  author_name: string | null;
  status: "new" | "relevant" | "archived";
  llm_reason: string | null;
  created_at: string | null;
  target_display_name: string | null;
  target_linkedin_url: string | null;
}

export interface PaginatedPosts {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: WatchlistPost[];
}

// Posts
export async function listPosts(params: {
  page?: number;
  page_size?: number;
  target_id?: number;
  status?: string;
}): Promise<PaginatedPosts> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.page_size) query.set("page_size", String(params.page_size));
  if (params.target_id !== undefined) query.set("target_id", String(params.target_id));
  if (params.status) query.set("status", params.status);
  const res = await api.get(`/admin/linkedin-profiles-watchlist/posts?${query.toString()}`);
  return res.data;
}

export async function updatePostStatus(
  id: number,
  status: "new" | "relevant" | "archived"
): Promise<WatchlistPost> {
  const res = await api.patch(`/admin/linkedin-profiles-watchlist/posts/${id}/status`, { status });
  return res.data;
}

export async function deletePost(id: number): Promise<void> {
  await api.delete(`/admin/linkedin-profiles-watchlist/posts/${id}`);
}

export async function bulkDeletePosts(olderThanDays: number): Promise<{ deleted: number; older_than_days: number }> {
  const res = await api.delete(`/admin/linkedin-profiles-watchlist/posts/bulk?older_than_days=${olderThanDays}`);
  return res.data;
}

// Job Leads

export interface WatchlistJobLead {
  id: number;
  internal_post_id: string | null;
  watch_target_id: number | null;
  linkedin_url: string | null;
  target_type: "personal" | "company" | null;
  linkedin_post_id: string | null;
  post_url: string;
  post_text: string | null;
  posted_at: string | null;
  author_name: string | null;
  author_id: string | null;
  account_type: string | null;
  hashtags: string | null;
  job_title: string | null;
  job_type: string | null;
  job_location: string | null;
  job_skills: string | null;
  job_description: string | null;
  application_method: string | null;
  application_url: string | null;
  ai_provider: string | null;
  ai_confidence: number | null;
  ai_reason: string | null;
  company_can_apply: boolean | null;
  company_analysis_metadata: string | null;
  remote_scope: string | null;
  remote_worldwide_analysis: string | null;
  status: string;
  source: string | null;
  created_at: string | null;
  target_display_name: string | null;
  target_linkedin_url: string | null;
}

export interface PaginatedLeads {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: WatchlistJobLead[];
}

export interface LeadFilters {
  watch_target_id?: number;
  status?: string;
  page: number;
  page_size: number;
}

export async function listJobLeads(params: {
  page?: number;
  page_size?: number;
  watch_target_id?: number;
  status?: string;
}): Promise<PaginatedLeads> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.page_size) query.set("page_size", String(params.page_size));
  if (params.watch_target_id !== undefined) query.set("watch_target_id", String(params.watch_target_id));
  if (params.status) query.set("status", params.status);
  const res = await api.get(`/admin/linkedin-profiles-watchlist/job-leads?${query.toString()}`);
  return res.data;
}

export async function updateLeadStatus(
  id: number,
  status: string
): Promise<WatchlistJobLead> {
  const res = await api.patch(`/admin/linkedin-profiles-watchlist/job-leads/${id}/status`, { status });
  return res.data;
}

export interface LeadHistoryEntry {
  id: number;
  lead_id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export async function getLeadHistory(id: number): Promise<LeadHistoryEntry[]> {
  const res = await api.get(`/admin/linkedin-profiles-watchlist/job-leads/${id}/history`);
  return res.data;
}

export async function getArchivedLeadsPreview(): Promise<{ lead_count: number }> {
  const res = await api.get("/admin/linkedin-profiles-watchlist/job-leads/cleanup/archived-preview");
  return res.data;
}

export async function deleteArchivedLeads(): Promise<{ deleted: number }> {
  const res = await api.delete("/admin/linkedin-profiles-watchlist/job-leads/cleanup/archived");
  return res.data;
}

// ── Lead Tracking (per-TM) ─────────────────────────────────────────────────

export interface WatchlistLeadTrackingRecord {
  tracking_id: number;
  lead_id: number;
  employee_id: number;
  status: string;
  tracking_created_at: string | null;
  tracking_updated_at: string | null;
  // lead fields
  id: number;
  internal_post_id: string | null;
  watch_target_id: number | null;
  linkedin_url: string | null;
  target_type: string | null;
  post_url: string;
  post_text: string | null;
  posted_at: string | null;
  author_name: string | null;
  job_title: string | null;
  job_type: string | null;
  job_location: string | null;
  job_skills: string | null;
  job_description: string | null;
  application_url: string | null;
  ai_confidence: number | null;
  ai_reason: string | null;
  ai_provider: string | null;
  company_can_apply: boolean | null;
  company_analysis_metadata: string | null;
  remote_scope: string | null;
  remote_worldwide_analysis: string | null;
  source: string | null;
  created_at: string | null;
  target_display_name: string | null;
  target_linkedin_url: string | null;
}

export interface PaginatedTracking {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: WatchlistLeadTrackingRecord[];
}

export interface TrackingHistoryEntry {
  id: number;
  tracking_id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export async function listLeadTracking(params: {
  page?: number;
  page_size?: number;
  employee_id?: number;
  watch_target_id?: number;
  status?: string;
  company_can_apply_only?: boolean;
}): Promise<PaginatedTracking> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.page_size) query.set("page_size", String(params.page_size));
  if (params.employee_id !== undefined) query.set("employee_id", String(params.employee_id));
  if (params.watch_target_id !== undefined) query.set("watch_target_id", String(params.watch_target_id));
  if (params.status) query.set("status", params.status);
  if (params.company_can_apply_only) query.set("company_can_apply_only", "true");
  const res = await api.get(`/admin/linkedin-profiles-watchlist/lead-tracking?${query.toString()}`);
  return res.data;
}

export async function listMyLeadTracking(params: {
  page?: number;
  page_size?: number;
  watch_target_id?: number;
  status?: string;
  company_can_apply_only?: boolean;
}): Promise<PaginatedTracking> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.page_size) query.set("page_size", String(params.page_size));
  if (params.watch_target_id !== undefined) query.set("watch_target_id", String(params.watch_target_id));
  if (params.status) query.set("status", params.status);
  if (params.company_can_apply_only) query.set("company_can_apply_only", "true");
  const res = await api.get(`/employee/linkedin-profiles-watchlist/lead-tracking?${query.toString()}`);
  return res.data;
}

export interface LeadTrackingTarget {
  watch_target_id: number;
  target_type: string;
  display_name: string;
}

export async function listLeadTrackingTargets(params: {
  employee_id?: number;
  company_can_apply_only?: boolean;
} = {}): Promise<LeadTrackingTarget[]> {
  const query = new URLSearchParams();
  if (params.employee_id !== undefined) query.set("employee_id", String(params.employee_id));
  if (params.company_can_apply_only) query.set("company_can_apply_only", "true");
  const res = await api.get(`/admin/linkedin-profiles-watchlist/lead-tracking/targets?${query.toString()}`);
  return res.data;
}

export async function listMyLeadTrackingTargets(params: {
  company_can_apply_only?: boolean;
} = {}): Promise<LeadTrackingTarget[]> {
  const query = new URLSearchParams();
  if (params.company_can_apply_only) query.set("company_can_apply_only", "true");
  const res = await api.get(`/employee/linkedin-profiles-watchlist/lead-tracking/targets?${query.toString()}`);
  return res.data;
}

export async function updateTrackingStatus(
  trackingId: number,
  status: string,
  notes?: string
): Promise<{ tracking_id: number; status: string }> {
  const res = await api.patch(`/admin/linkedin-profiles-watchlist/lead-tracking/${trackingId}/status`, {
    status,
    ...(notes !== undefined ? { notes } : {}),
  });
  return res.data;
}

export async function updateMyTrackingStatus(
  trackingId: number,
  status: string,
  notes?: string
): Promise<{ tracking_id: number; status: string }> {
  const res = await api.patch(`/employee/linkedin-profiles-watchlist/lead-tracking/${trackingId}/status`, {
    status,
    ...(notes !== undefined ? { notes } : {}),
  });
  return res.data;
}

export async function getTrackingHistory(
  trackingId: number,
  isAdmin: boolean
): Promise<TrackingHistoryEntry[]> {
  const prefix = isAdmin ? "admin" : "employee";
  const res = await api.get(`/${prefix}/linkedin-profiles-watchlist/lead-tracking/${trackingId}/history`);
  return res.data;
}

export async function getArchivedTrackingPreview(isAdmin: boolean): Promise<{ lead_count: number }> {
  const prefix = isAdmin ? "admin" : "employee";
  const res = await api.get(`/${prefix}/linkedin-profiles-watchlist/lead-tracking/cleanup/archived-preview`);
  return res.data;
}

export async function deleteArchivedTracking(isAdmin: boolean): Promise<{ deleted: number }> {
  const prefix = isAdmin ? "admin" : "employee";
  const res = await api.delete(`/${prefix}/linkedin-profiles-watchlist/lead-tracking/cleanup/archived`);
  return res.data;
}
